import { ref, computed, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import moment from 'moment-timezone'
import router from '@/router'
import ability from '@/libs/acl/ability'
import axios from '@/libs/axios'
import store from '@/store'
import { debounce } from 'lodash'
import useFilterCounter from '@/components/Filters/useFiltersCount'

export default function useList() {
  store.dispatch('camera_type/getAll')
  store.dispatch('unit/getAll', { with: 'router:id,id_tenant' })

  const cameraTypeOptions = computed(() => store.getters['camera_type/getSelectedItems'])

  const refListTable = ref(null)

  const toast = useToast()

  const listFilter = ref(null)

  if (router.currentRoute.params.type)
    listFilter.value = parseInt(router.currentRoute.type)

  const tableColumns = [
    {
      label: 'actions.name',
      key: 'actions'
    },
    {
      label: 'luxriot.name',
      key: 'title',
      sortable: true
    },
    {
      label: 'luxriot.alias',
      key: 'alias',
      sortable: true
    },
    {
      label: 'camera_proxy.last_connection',
      key: 'camera.last_connection',
      sortable: true
    },
    {
      label: 'luxriot.units',
      key: 'camera.units',
      formatter: (value, key, item) => {
        if (value) return value.map(item => { return item.description }).join(', ')
        return ""
      }
    },
    {
      label: 'luxriot.center',
      key: 'center.name'
    },
    {
      label: 'luxriot.sector',
      key: 'sector.name'
    },
    {
      label: 'luxriot.idCameraProxy',
      key: 'id_camera_proxy'
    },
    {
      label: 'luxriot.master_account',
      key: 'luxriotMasterAccount.title'
    },
    {
      label: 'luxriot.type',
      key: 'type.type'
    },
    {
      label: 'luxriot.channel',
      key: 'channel_info'
    },
    {
      label: 'luxriot.delayed_record_minutes',
      key: 'delayed_record_minutes'
    },
    {
      label: 'luxriot.forward_record_minutes',
      key: 'forward_record_minutes'
    },
  ]

  const perPage = ref(10)
  const totalList = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)
  const units = computed(() => store.getters['unit/getSelectedItems'])
  const luxriotMasterAccountId = ref(null)

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  })

  const fetchList = async () => {
    let params = {
      q: searchQuery.value,
      max: perPage.value,
      'page[n]': currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      type: listFilter.value,
      master_account_id: luxriotMasterAccountId.value
    }

    let { data } = await axios.get('/v1/services/luxriot/cameras', { params })
    let { data: dataApi, meta, success } = data

    if (!success) {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Error obteniendo la información',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })

      return []
    }

    totalList.value = meta.total

    return dataApi
  }

  const formatDateAssigned = value => {
    const formattedDate = moment(value).format('DD/MM/YYYY HH:mm:ss')
    return formattedDate
  }

  const refetchData = () => {
    refListTable.value.refresh()
  }

  const contentToast = (message, type) => toast({
    component: ToastificationContent,
    props: {
      title: message,
      icon: 'CheckIcon',
      variant: type
    }
  })


  watch([listFilter, currentPage, perPage], () => {
    refetchData()
  })

  watch([searchQuery], debounce(() => {
    refetchData()
  }, 500))

  // filters counter ============================
  const filters = ref({});
  const filtersCount = useFilterCounter(filters);
  const buildFilters = () => {
    filters.value = {
      master_account_id: luxriotMasterAccountId.value
    };
    refetchData();
  }
  const clearSearchFilters = () => {
    luxriotMasterAccountId.value = null;
    buildFilters();
  }
  // ============================================

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    formatDateAssigned,
    luxriotMasterAccountId,
    cameraTypeOptions,

    listFilter,

    refetchData,
    contentToast,
    units,
    filtersCount,
    buildFilters,
    clearSearchFilters
  }
}
